import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Download, Funnel, FloppyDisk, Trash, CheckCircle, WarningCircle, Copy, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITATION_STATUSES = [
  ["", "Any status"],
  ["verified", "Verified"],
  ["partially_verified", "Partially verified"],
  ["not_verified", "Not verified"],
  ["not_processed", "Not processed"],
  ["failed", "Failed"],
];
const APPROVAL_STATUSES = [
  ["", "Any approval"],
  ["pending", "Pending"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
];
const EVIDENCE_TYPES = [
  ["", "All types"],
  ["pivotal_rct", "Pivotal RCT"],
  ["meta_analysis", "Meta-analysis"],
  ["indirect_comparison", "Indirect comparison"],
  ["single_arm", "Single-arm"],
  ["registry", "Registry"],
  ["post_marketing", "Post-marketing"],
  ["economic_model", "Economic model"],
  ["budget_impact", "Budget impact"],
  ["cost_effectiveness", "Cost-effectiveness"],
];

const emptyFilters = () => ({
  evidence_type: "", country: "", citation_status: "",
  approval_status: "", reviewer: "", verified_only: false,
});

function Chip({ tone = "slate", children, testId }) {
  const tones = {
    slate: "bg-slate-100 text-slate-600 border-slate-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-300",
    red: "bg-red-50 text-red-700 border-red-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
  };
  return (
    <span data-testid={testId}
      className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tones[tone]}`}>
      {children}
    </span>
  );
}

function statusTone(status) {
  if (status === "verified") return "green";
  if (status === "partially_verified") return "blue";
  if (status === "not_verified") return "red";
  if (status === "failed") return "red";
  return "slate";
}

export function EvidenceRegisterV2({ productId }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("all"); // all | unverified | duplicates | exceptions
  const [rows, setRows] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [savedViews, setSavedViews] = useState([]);
  const [filters, setFilters] = useState(emptyFilters());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [viewName, setViewName] = useState("");
  const [approving, setApproving] = useState(null); // asset row for exception dialog
  const [approveReason, setApproveReason] = useState("");
  const [approveBusy, setApproveBusy] = useState(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const p = { product_id: productId, ...filters };
      Object.keys(p).forEach((k) => { if (p[k] === "" || p[k] === false) delete p[k]; });
      let url = `${API}/inventory-v2/items`;
      if (tab === "unverified")  url = `${API}/inventory-v2/unverified`;
      if (tab === "duplicates")  url = `${API}/inventory-v2/duplicates`;
      const r = await axios.get(url, { params: tab === "all" ? p : { product_id: productId } });
      setRows(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load inventory"); }
    finally { setLoading(false); }
  }, [productId, filters, tab]);

  const loadMeta = useCallback(async () => {
    try {
      const [sv, ex] = await Promise.all([
        axios.get(`${API}/inventory-v2/saved-views`, { params: { product_id: productId } }),
        axios.get(`${API}/inventory-v2/exceptions`,  { params: { product_id: productId } }),
      ]);
      setSavedViews(sv.data || []); setExceptions(ex.data || []);
    } catch (e) { /* silent */ }
  }, [productId]);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const counts = useMemo(() => ({
    total: rows.length,
    verified: rows.filter((r) => r.is_verified).length,
    unverified: rows.filter((r) => !r.is_verified && !r.is_duplicate).length,
    duplicates: rows.filter((r) => r.is_duplicate).length,
    exceptions: rows.filter((r) => r.exception_approved).length,
  }), [rows]);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const p = { product_id: productId, ...filters };
      Object.keys(p).forEach((k) => { if (p[k] === "" || p[k] === false) delete p[k]; });
      const r = await axios.get(`${API}/inventory-v2/export/xlsx`,
        { params: p, responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `evidence-inventory-${productId}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Excel exported");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const saveView = async () => {
    if (!viewName.trim()) return toast.error("Please name this view");
    setSavingView(true);
    try {
      await axios.post(`${API}/inventory-v2/saved-views`,
        { product_id: productId, name: viewName.trim(), filters });
      toast.success("View saved");
      setViewName(""); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save view"); }
    finally { setSavingView(false); }
  };

  const applyView = (v) => { setFilters({ ...emptyFilters(), ...v.filters }); setTab("all"); };
  const deleteView = async (id) => {
    await axios.delete(`${API}/inventory-v2/saved-views/${id}`);
    toast.success("Saved view removed");
    loadMeta();
  };

  const submitException = async () => {
    if (approveReason.trim().length < 10) return toast.error("Reason must be at least 10 characters");
    setApproveBusy(true);
    try {
      await axios.post(
        `${API}/inventory-v2/assets/${approving.asset_id}/approve-exception?product_id=${productId}`,
        { reason: approveReason.trim() });
      toast.success("Exception approved — asset will now count toward readiness");
      setApproving(null); setApproveReason("");
      loadRows(); loadMeta();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve exception"); }
    finally { setApproveBusy(false); }
  };

  const visible = tab === "exceptions" ? rows.filter((r) => r.exception_approved) : rows;

  return (
    <section data-testid="evidence-register-v2" className="bg-white border border-slate-200">
      <header className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
        <div>
          <p className="font-heading font-bold text-sm tracking-tight">Evidence Register</p>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed max-w-3xl">
            One row per asset with the full study → publication → claim hierarchy, quality/status and citation identity.
            Only <em>verified</em> or exception-approved evidence counts toward readiness scores.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button data-testid="inventory-export-xlsx" onClick={exportXlsx} disabled={exporting}
            className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
            {exporting ? <CircleNotch size={11} className="animate-spin" /> : <Download size={11} />} Export Excel
          </button>
        </div>
      </header>

      <div data-testid="inventory-v2-counts" className="grid grid-cols-2 md:grid-cols-5 divide-x divide-slate-100 border-b border-slate-200">
        {[
          ["Total", counts.total, "slate"],
          ["Verified", counts.verified, "green"],
          ["Unverified", counts.unverified, "amber"],
          ["Duplicates", counts.duplicates, "violet"],
          ["Exceptions", counts.exceptions, "blue"],
        ].map(([label, value]) => (
          <div key={label} className="px-4 py-3">
            <p className="overline-label text-slate-400">{label}</p>
            <p className="font-heading font-extrabold text-xl mt-0.5">{value}</p>
          </div>
        ))}
      </div>

      <div className="px-5 pt-3 flex flex-wrap items-center gap-1 border-b border-slate-100">
        {[
          ["all", `All (${counts.total})`],
          ["unverified", `Unverified (${counts.unverified})`],
          ["duplicates", `Duplicates (${counts.duplicates})`],
          ["exceptions", `Exceptions (${counts.exceptions})`],
        ].map(([k, label]) => (
          <button key={k} data-testid={`register-tab-${k}`} onClick={() => setTab(k)}
            className={`text-[11px] font-semibold px-3 py-1.5 border-b-2 ${tab === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "all" && (
        <div data-testid="inventory-v2-filters" className="px-5 py-3 flex flex-wrap items-center gap-2 bg-slate-50/60 border-b border-slate-100">
          <Funnel size={13} className="text-slate-400" />
          <Select value={filters.evidence_type || "any"} onValueChange={(v) => setFilters({ ...filters, evidence_type: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-evidence-type" className="h-8 w-40 text-xs bg-white"><SelectValue placeholder="Evidence type" /></SelectTrigger>
            <SelectContent className="bg-white">
              {EVIDENCE_TYPES.map(([k, l]) => <SelectItem key={k || "any"} value={k || "any"}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.citation_status || "any"} onValueChange={(v) => setFilters({ ...filters, citation_status: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-citation-status" className="h-8 w-40 text-xs bg-white"><SelectValue placeholder="Citation status" /></SelectTrigger>
            <SelectContent className="bg-white">
              {CITATION_STATUSES.map(([k, l]) => <SelectItem key={k || "any"} value={k || "any"}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.approval_status || "any"} onValueChange={(v) => setFilters({ ...filters, approval_status: v === "any" ? "" : v })}>
            <SelectTrigger data-testid="filter-approval-status" className="h-8 w-40 text-xs bg-white"><SelectValue placeholder="Approval status" /></SelectTrigger>
            <SelectContent className="bg-white">
              {APPROVAL_STATUSES.map(([k, l]) => <SelectItem key={k || "any"} value={k || "any"}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid="filter-reviewer" placeholder="Reviewer email" className="h-8 w-44 text-xs bg-white"
            value={filters.reviewer} onChange={(e) => setFilters({ ...filters, reviewer: e.target.value })} />
          <Input data-testid="filter-country" placeholder="Country id" className="h-8 w-32 text-xs bg-white"
            value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} />
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 ml-1">
            <input type="checkbox" data-testid="filter-verified-only" checked={filters.verified_only}
              onChange={(e) => setFilters({ ...filters, verified_only: e.target.checked })} />
            Verified only
          </label>
          <button data-testid="filters-reset" onClick={() => setFilters(emptyFilters())}
            className="text-[11px] text-slate-500 hover:text-slate-900 ml-auto">Reset</button>
          <div className="flex items-center gap-1">
            <Input data-testid="saved-view-name" placeholder="Save this view as…" className="h-8 w-44 text-xs bg-white"
              value={viewName} onChange={(e) => setViewName(e.target.value)} />
            <button data-testid="saved-view-save" onClick={saveView} disabled={savingView}
              className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-2.5 py-1.5 hover:bg-slate-50 flex items-center gap-1.5 disabled:opacity-50">
              {savingView ? <CircleNotch size={11} className="animate-spin" /> : <FloppyDisk size={11} />} Save
            </button>
          </div>
        </div>
      )}

      {tab === "all" && savedViews.length > 0 && (
        <div data-testid="saved-views" className="px-5 py-2 flex flex-wrap items-center gap-2 border-b border-slate-100">
          <span className="overline-label text-slate-400">Saved views</span>
          {savedViews.map((v) => (
            <span key={v.id} className="inline-flex items-center gap-1 text-[11px] border border-slate-200 pl-2 rounded-full">
              <button data-testid={`view-apply-${v.id}`} onClick={() => applyView(v)} className="font-semibold text-slate-700 hover:text-slate-900">{v.name}</button>
              <button data-testid={`view-delete-${v.id}`} onClick={() => deleteView(v.id)} className="text-slate-300 hover:text-red-600 px-1.5 py-1"><Trash size={11} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="inventory-v2-table">
          <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-400">
            <tr>
              <th className="text-left px-4 py-2.5">Asset / Study → Publication</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">Population / Comparator</th>
              <th className="text-left px-3 py-2.5">Quality</th>
              <th className="text-left px-3 py-2.5">Citation</th>
              <th className="text-left px-3 py-2.5">Reviewer</th>
              <th className="text-left px-3 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No records match the current view.</td></tr>
            )}
            {!loading && visible.map((r) => (
              <tr key={r.asset_id} data-testid={`row-${r.asset_id}`} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 align-top">
                  <p className="font-semibold text-slate-800">{r.name || r.asset_id}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {r.study_name ? <>Study: <span className="text-slate-500">{r.study_name}</span></> : "Study: —"}
                    {" · "}
                    {r.publication_title ? <>Publication: <span className="text-slate-500 italic">&ldquo;{r.publication_title.slice(0, 70)}{r.publication_title.length > 70 ? "…" : ""}&rdquo;</span></> : "Unpublished"}
                  </p>
                  {(r.year || r.journal) && (
                    <p className="font-mono-data text-[10px] text-slate-400 mt-0.5">
                      {(r.authors || []).slice(0, 3).join(", ")}{r.authors?.length > 3 ? " et al." : ""}
                      {r.year ? ` (${r.year})` : ""}{r.journal ? ` · ${r.journal}` : ""}
                      {r.doi ? ` · ${r.doi}` : ""}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3 align-top text-[11px] text-slate-600">
                  {r.evidence_type || "—"}
                  {r.study_design && <p className="text-[10px] text-slate-400 mt-0.5">{r.study_design}</p>}
                </td>
                <td className="px-3 py-3 align-top text-[11px] text-slate-600">
                  {r.population || "—"}
                  {r.comparator && <p className="text-[10px] text-slate-400 mt-0.5">vs. {r.comparator}</p>}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.evidence_quality
                    ? <Chip tone={r.evidence_quality === "high" ? "green" : r.evidence_quality === "moderate" ? "blue" : "amber"}>{r.evidence_quality}</Chip>
                    : <span className="text-[10px] text-slate-400">—</span>}
                  {r.relevance && <p className="text-[10px] text-slate-400 mt-1">Relevance: {r.relevance}</p>}
                </td>
                <td className="px-3 py-3 align-top">
                  <Chip tone={statusTone(r.citation_status)} testId={`citation-status-${r.asset_id}`}>
                    {r.citation_status?.replace(/_/g, " ")}
                  </Chip>
                  {r.source_link && (
                    <a href={r.source_link} target="_blank" rel="noreferrer"
                      className="block text-[10px] text-blue-600 mt-1 hover:underline truncate max-w-[120px]">Source</a>
                  )}
                </td>
                <td className="px-3 py-3 align-top text-[11px] text-slate-600">{r.reviewer || "—"}</td>
                <td className="px-3 py-3 align-top">
                  <div className="flex flex-col gap-1">
                    <Chip tone={r.approval_status === "approved" ? "green" : r.approval_status === "rejected" ? "red" : "slate"}>
                      {r.approval_status}
                    </Chip>
                    {r.is_verified && <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700"><CheckCircle size={10} weight="fill" /> counts toward readiness</span>}
                    {!r.is_verified && !r.exception_approved && !r.is_duplicate &&
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700"><WarningCircle size={10} weight="fill" /> excluded from readiness</span>}
                    {r.exception_approved && <span className="inline-flex items-center gap-1 text-[10px] text-blue-700"><ShieldCheck size={10} weight="fill" /> exception approved</span>}
                    {r.is_duplicate && <span className="inline-flex items-center gap-1 text-[10px] text-violet-700"><Copy size={10} weight="fill" /> duplicate</span>}
                  </div>
                </td>
                <td className="px-4 py-3 align-top text-right">
                  {!r.is_verified && !r.exception_approved && !r.is_duplicate && isAdmin && (
                    <button data-testid={`approve-exception-${r.asset_id}`} onClick={() => { setApproving(r); setApproveReason(""); }}
                      className="text-[11px] font-semibold text-slate-700 border border-slate-300 px-2.5 py-1.5 hover:bg-slate-50">
                      Approve exception
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tab === "exceptions" && exceptions.length > 0 && (
        <div data-testid="exception-audit" className="px-5 py-4 border-t border-slate-200">
          <p className="overline-label text-slate-400 mb-2">Exception audit log</p>
          <ul className="space-y-1 text-[11px] text-slate-600">
            {exceptions.map((e) => (
              <li key={e.id} data-testid={`exception-${e.id}`} className="border-l-2 border-blue-300 pl-2">
                <b>{e.approver_email}</b> approved <code className="text-slate-500">{e.asset_id}</code>
                <span className="text-slate-400"> · {new Date(e.at).toLocaleString()}</span>
                <p className="text-slate-500 mt-0.5">{e.reason}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {approving && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
             onClick={() => !approveBusy && setApproving(null)}>
          <div data-testid="exception-dialog" className="bg-white border border-slate-200 max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
            <p className="font-heading font-bold text-sm">Approve readiness exception</p>
            <p className="text-xs text-slate-500 mt-1">Asset: <b>{approving.name}</b></p>
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              As a senior reviewer you are overriding the unverified status and permitting this asset to count toward
              readiness scores. This action is audit-logged and permanent unless another admin reverses it.
            </p>
            <textarea data-testid="exception-reason" rows={3} placeholder="Reason (min 10 chars)"
              value={approveReason} onChange={(e) => setApproveReason(e.target.value)}
              className="mt-3 w-full text-xs border border-slate-300 rounded p-2 focus:outline-slate-900" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setApproving(null)} disabled={approveBusy}
                className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
              <button data-testid="exception-submit" onClick={submitException} disabled={approveBusy}
                className="text-[11px] font-semibold text-white bg-slate-900 px-3 py-1.5 hover:bg-slate-800 flex items-center gap-1.5 disabled:opacity-50">
                {approveBusy && <CircleNotch size={11} className="animate-spin" />} Approve exception
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
